import { makeAutoObservable, runInAction } from "mobx";
import { TaskHttpRepository } from "./task_http_repository";
import {
  TaskValidationModel,
  TypesTasks,
  TaskOnlyText,
  TaskOnlyNumber,
  TaskOneOfSeveral,
  TaskSeveralOSeveral,
} from "./task_model";

export const taskLabels: Record<TypesTasks, string> = {
  onlyText: "Текстовый ответ",
  onlyNumber: "Числовой ответ",
  oneOfSeveral: "Один вариант",
  severalOSeveral: "Несколько вариантов",
};
export function newTask(type: TypesTasks) {
  switch (type) {
    case TypesTasks.onlyNumber:
      return new TaskOnlyNumber();
    case TypesTasks.oneOfSeveral:
      return new TaskOneOfSeveral();
    case TypesTasks.severalOSeveral:
      return new TaskSeveralOSeveral();
    default:
      return new TaskOnlyText();
  }
}
interface TaskEditorData {
  _id?: string;
  status: "draft" | "published";
  scoreAmount: number;
  tags: string[];
  randomEligible?: boolean;
  task: Pick<
    TaskValidationModel["task"],
    "type" | "title" | "description" | "solution" | "answers"
  >;
}
export interface AnswerDraft {
  text: string;
  isRight: boolean;
}
export class TaskEditorStore {
  model: TaskEditorData = {
    status: "draft",
    scoreAmount: 0,
    tags: [],
    task: { ...new TaskOnlyText() },
  };
  answers: AnswerDraft[] = [];
  score = "0";
  tagInput = "";
  baseline = "";
  loading = false;
  saving = false;
  uploading = false;
  ready = false;
  error = "";
  notice = "";
  fieldErrors: Record<string, string> = {};
  constructor(public repository = new TaskHttpRepository()) {
    makeAutoObservable(this, { repository: false });
  }
  get snapshot() {
    return JSON.stringify({
      model: this.model,
      answers: this.answers,
      score: this.score,
      tagInput: this.tagInput,
    });
  }
  get dirty() {
    return this.ready && this.snapshot !== this.baseline;
  }
  async load(id?: string) {
    this.loading = true;
    this.ready = false;
    this.error = "";
    try {
      if (!id) {
        this.model = {
          status: "draft",
          scoreAmount: 0,
          tags: [],
          task: { ...new TaskOnlyText() },
        };
        this.answers = [];
        this.score = "0";
      }
      this.tagInput = "";
      this.fieldErrors = {};
      this.notice = "";
      if (id) {
        const result = await this.repository.getOne(id);
        if (result.isFailure()) throw result.error;
        const record = result.getOrThrow();
        if (!record.task)
          throw new Error(
            "У старой записи нет содержимого. Восстановите его из исходного задания.",
          );
        runInAction(() => {
          this.model = {
            ...record,
            task: { ...record.task },
            status: record.status || "draft",
          };
          this.score = String(record.scoreAmount);
          this.answers = record.task.answers.map((answer) =>
            typeof answer === "object"
              ? { ...answer }
              : { text: String(answer), isRight: false },
          );
        });
      }
      runInAction(() => {
        this.ready = true;
        this.baseline = this.snapshot;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
  changeType(type: TypesTasks) {
    if (type === this.model.task.type) return;
    const { title, description, solution } = this.model.task;
    this.model.task = { ...newTask(type), title, description, solution };
    this.answers = [];
    this.fieldErrors = {};
  }
  setRandomEligible(value: boolean) {
    this.model.randomEligible = value;
  }
  setTitle(value: string) {
    this.model.task.title = value;
  }
  setDescription(value: string) {
    this.model.task.description = value;
  }
  setSolution(value: string) {
    this.model.task.solution = value;
  }
  async uploadImage(file: File): Promise<string> {
    if (this.uploading) throw new Error("Дождитесь загрузки изображения");
    this.uploading = true;
    try {
      const result = await this.repository.uploadImage(file);
      if (result.isFailure()) throw result.error;
      return result.getOrThrow().url;
    } finally {
      runInAction(() => {
        this.uploading = false;
      });
    }
  }
  setScore(value: string) {
    this.score = value;
  }
  setTagInput(value: string) {
    this.tagInput = value;
  }
  setAnswerText(index: number, value: string) {
    this.answers[index].text = value;
  }
  addAnswer() {
    this.answers.push({ text: "", isRight: false });
  }
  removeAnswer(index: number) {
    this.answers.splice(index, 1);
  }
  moveAnswer(index: number, offset: number) {
    const target = index + offset;
    if (target < 0 || target >= this.answers.length) return;
    [this.answers[index], this.answers[target]] = [
      this.answers[target],
      this.answers[index],
    ];
  }
  markAnswer(index: number, checked: boolean) {
    if (this.model.task.type === TypesTasks.oneOfSeveral)
      this.answers.forEach((answer) => {
        answer.isRight = false;
      });
    this.answers[index].isRight = checked;
  }
  addTag() {
    const tag = this.tagInput.trim();
    if (tag && !this.model.tags.includes(tag)) this.model.tags.push(tag);
    this.tagInput = "";
  }
  removeTag(tag: string) {
    this.model.tags = this.model.tags.filter((value) => value !== tag);
  }
  validate() {
    const errors: Record<string, string> = {};
    for (const field of ["description", "solution"] as const) {
      if ((this.model.task[field]?.length || 0) > 100000)
        errors[`task.${field}`] = "Максимум 100 000 символов";
    }
    if (!this.model.task.title.trim())
      errors["task.title"] = "Введите название задания";
    if (
      !this.score.trim() ||
      !Number.isFinite(Number(this.score)) ||
      Number(this.score) < 0
    )
      errors.scoreAmount = "Баллы должны быть конечным числом не меньше нуля";
    if (this.tagInput.trim())
      errors.tags = "Добавьте введённый тег кнопкой или очистите поле";
    if (!this.answers.length)
      errors["task.answers"] = "Добавьте хотя бы один ответ";
    this.answers.forEach((answer, index) => {
      if (!answer.text.trim())
        errors[`task.answers.${index}`] = "Введите ответ";
      else if (
        this.model.task.type === TypesTasks.onlyNumber &&
        !Number.isFinite(Number(answer.text))
      )
        errors[`task.answers.${index}`] =
          "Введите конечное число, например −3 или 1.5";
    });
    const count = this.answers.filter((answer) => answer.isRight).length;
    if (this.model.task.type === TypesTasks.oneOfSeveral && count !== 1)
      errors["task.answers"] = "Выберите один правильный вариант";
    if (this.model.task.type === TypesTasks.severalOSeveral && count === 0)
      errors["task.answers"] = "Отметьте хотя бы один правильный вариант";
    this.fieldErrors = errors;
    return Object.keys(errors).length === 0;
  }
  payload(status: "draft" | "published"): TaskValidationModel {
    const task = Object.assign(newTask(this.model.task.type), {
      title: this.model.task.title,
      description: this.model.task.description,
      solution: this.model.task.solution,
    });
    if (task.type === TypesTasks.onlyText)
      task.answers = this.answers.map((a) => a.text);
    else if (task.type === TypesTasks.onlyNumber)
      task.answers = this.answers.map((a) => Number(a.text));
    else
      task.answers = this.answers.map((a) => ({
        text: a.text,
        isRight: a.isRight,
      }));
    return Object.assign(new TaskValidationModel(), {
      _id: this.model._id,
      status,
      scoreAmount: Number(this.score),
      randomEligible: this.model.randomEligible !== false,
      tags: [...this.model.tags],
      task,
    });
  }
  async save(status: "draft" | "published") {
    if (!this.ready || this.saving || this.loading || this.uploading) return;
    this.error = "";
    this.notice = "";
    if (!this.validate()) {
      this.error = "Проверьте отмеченные поля";
      return;
    }
    this.saving = true;
    const payload = this.payload(status);
    try {
      const result = await this.repository.save(payload);
      if (result.isFailure()) throw result.error;
      const saved = result.getOrThrow();
      runInAction(() => {
        this.model = {
          ...payload,
          task: { ...payload.task },
          _id: saved.id || saved._id || payload._id,
        };
        this.baseline = this.snapshot;
        this.notice =
          status === "published" ? "Задание опубликовано" : "Черновик сохранён";
      });
      return this.model._id;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
        // The API returns recursive field paths; keep the full error visible too.
        this.error.split("; ").forEach((part) => {
          const match = part.match(/^([\w.]+): (.+)$/);
          if (match) this.fieldErrors[match[1]] = match[2];
        });
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }
}
